import React, { useCallback, useEffect, useState } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import {
	WCL_GRID_MAX_WIDTH,
	WCL_GRID_PADDING_LG,
	WCL_GRID_PADDING_SM,
} from '@/libraries/design/foundations/grid/src/base';
import type { IconSize } from '@/libraries/design/foundations/icon';
import { isValidLocale } from '@/utils/localization';

import { NavigationTabsList } from './navigation-tabs-list';

export type NavigationTabsAppearance = 'wide' | 'compact';
export type NavigationTabsLayout = 'stacked' | 'overflow';
export type NavigationTabDataType = {
	id: string;
	title: string;
	href: string;
	iconUrl?: string;
	iconSize?: IconSize;
	disableIconPadding?: boolean;
	tabComponent: React.ReactElement;
};
export type NavigationTabsSectionProps = {
	tabsData: NavigationTabDataType[];
	appearance?: NavigationTabsAppearance;
	layout?: NavigationTabsLayout;
	useHistoryRouting?: boolean;
	eyebrow?: string;
	showControls?: boolean;
};

const sectionCss = css({
	width: '100%',
	maxWidth: '100%',
	minWidth: 0,
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
});
const stackedSectionCss = css({
	padding: '20px 0',
	alignItems: 'center',
	gap: token('space.300'),
});
const gridMaxWidth = WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2;
const navigationTabsGridFrameCss = css({
	boxSizing: 'border-box',
	width: '100%',
	maxWidth: '100%',
	marginInline: 'auto',
	paddingInlineStart: `${WCL_GRID_PADDING_SM}px`,
	paddingInlineEnd: `${WCL_GRID_PADDING_SM}px`,
	[media.only.sm]: {
		paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
		paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
	},
	[media.above.md]: {
		paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
		paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
		maxWidth: `${gridMaxWidth}px`,
	},
});
const navigationTabsGridCss = css({
	display: 'grid',
	gridTemplateColumns: 'repeat(12, 1fr)',
});
const navigationTabsGridSpanCss = css({
	gridColumn: '1 / -1',
	minWidth: 0,
});
const contentCss = css({ width: '100%', minWidth: 0 });

const normalizePathname = (href: string) => {
	const parsed = new URL(href, window.location.origin);
	const segments = parsed.pathname.split('/');
	if (isValidLocale(segments[1] ?? '')) segments.splice(1, 1);
	const pathname = segments.join('/').replace(/\/+$/, '');
	return pathname || '/';
};
const isEligibleHistoryActivation = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
	const target = event.currentTarget.target.trim().toLowerCase();
	return (
		!event.defaultPrevented &&
		event.button === 0 &&
		!event.metaKey &&
		!event.altKey &&
		!event.ctrlKey &&
		!event.shiftKey &&
		(!target || target === '_self') &&
		!event.currentTarget.hasAttribute('download') &&
		new URL(href, window.location.origin).origin === window.location.origin
	);
};

export const NavigationTabsSection = ({
	tabsData,
	appearance = 'wide',
	layout = 'stacked',
	useHistoryRouting = false,
	eyebrow,
	showControls = layout === 'overflow',
}: NavigationTabsSectionProps) => {
	const [activeHref, setActiveHref] = useState<string>();
	const resolveActiveHref = useCallback(() => {
		const pathname = normalizePathname(window.location.href);
		return tabsData.find((tab) => {
			const destination = new URL(tab.href, window.location.origin);
			return (
				destination.origin === window.location.origin && normalizePathname(tab.href) === pathname
			);
		})?.href;
	}, [tabsData]);
	useEffect(() => {
		const synchronize = () => setActiveHref(resolveActiveHref());
		synchronize();
		window.addEventListener('popstate', synchronize);
		return () => window.removeEventListener('popstate', synchronize);
	}, [resolveActiveHref]);
	const onLinkClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
			if (!isEligibleHistoryActivation(event, href)) return;
			event.preventDefault();
			const destination = new URL(href, window.location.origin);
			window.history.pushState(
				null,
				'',
				`${destination.pathname}${destination.search}${destination.hash}`,
			);
			setActiveHref(resolveActiveHref());
		},
		[resolveActiveHref],
	);
	const activeTab = tabsData.find((tab) => tab.href === activeHref);
	return (
		<section id="navigation-tabs" css={[sectionCss, layout === 'stacked' && stackedSectionCss]}>
			<NavigationTabsList
				tabsData={tabsData}
				appearance={appearance}
				layout={layout}
				eyebrow={eyebrow}
				showControls={showControls}
				activeHref={activeHref}
				onLinkClick={useHistoryRouting ? onLinkClick : undefined}
			/>
			<div
				css={[navigationTabsGridFrameCss, navigationTabsGridCss]}
				data-testid="navigation-tabs-grid"
			>
				<div css={[contentCss, navigationTabsGridSpanCss]}>{activeTab?.tabComponent}</div>
			</div>
		</section>
	);
};
